import type React from 'react';

import { Text } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useCountryCode } from 'src/app/shared/hooks/l10n';

export const DmOverview: React.FC<{
	dmSources: 'none' | 'partial_manual_add' | 'only_auto_add' | 'no_email_dms';
}> = ({ dmSources }) => {
	const { translate } = useI18n();
	const countryCode = useCountryCode();
	// Some copy references features that are only available in the US
	const isUSUser = countryCode === 'US';

	const t = {
		definition: translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.definition'),
		noneTip: isUSUser ? translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.none_tip') : undefined,
		autoAddedDefinition: translate(
			'corgi.petition.starter_dashboard_v2.decision_makers.overview.auto_added_definition',
		),
		edit: translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.edit'),
		autoAddedTip: translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.auto_added_tip'),
		manualAddedTip: translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.manual_added_tip'),
		emailAction: translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.email_action'),
		signatureGoal: isUSUser
			? translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.signature_goal')
			: undefined,
	};

	return (
		<div data-testid="dm-overview">
			<div className="mb-4">
				<Text as="h3" fontWeight="bold" className="text-size-heading-small">
					{translate('corgi.petition.starter_dashboard_v2.decision_makers.overview.heading')}
				</Text>
			</div>

			<div>
				{dmSources === 'none' && <NoneDm textDefinition={t.definition} textTip={t.noneTip} />}
				{dmSources === 'only_auto_add' && (
					<AutoAddedDm
						textDefinition={t.autoAddedDefinition}
						textTip={t.autoAddedTip}
						textEdit={t.edit}
						textGoal={t.signatureGoal}
						textEmailAction={t.emailAction}
					/>
				)}
				{(dmSources === 'partial_manual_add' || dmSources === 'no_email_dms') && (
					<ManualAddDm textTip={t.manualAddedTip} textAction={t.emailAction} textGoal={t.signatureGoal} />
				)}
			</div>
		</div>
	);
};

const NoneDm: React.FC<{ textDefinition: string; textTip?: string }> = ({ textDefinition, textTip }) => {
	return (
		<>
			<Text as="p" className="wrap-break-word break-keep">
				{textDefinition}
			</Text>
			{textTip && (
				<Text as="p" fontWeight="bold" className="mt-4">
					{textTip}
				</Text>
			)}
		</>
	);
};

const AutoAddedDm: React.FC<{
	textDefinition: string;
	textTip: string;
	textEdit: string;
	textGoal?: string;
	textEmailAction: string;
}> = ({ textDefinition, textTip, textEdit, textGoal, textEmailAction }) => {
	return (
		<>
			<Text as="p">
				<Text as="strong">{textDefinition}</Text> {textEdit}
			</Text>
			<br />
			<Text as="p" className="wrap-break-word break-keep">
				{textTip} <Text as="strong">{textEmailAction}</Text>
			</Text>
			{textGoal && (
				<>
					<br />
					<Text as="p">{textGoal}</Text>
				</>
			)}
		</>
	);
};

const ManualAddDm: React.FC<{ textTip: string; textAction: string; textGoal?: string }> = ({
	textTip,
	textAction,
	textGoal,
}) => {
	return (
		<>
			<Text as="p" className="wrap-break-word break-keep">
				{textTip} <Text as="strong">{textAction}</Text>
			</Text>
			{textGoal && (
				<>
					<br />
					<Text as="p">{textGoal}</Text>
				</>
			)}
		</>
	);
};
